<template>
  <TransitionRoot as="template" :show="open">
    <Dialog class="fixed inset-0 z-[9999]" @close="$emit('close')">
      <TransitionChild
        as="div"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
        class="fixed inset-0 bg-gray-500/75 transition-opacity"
      />

      <div class="fixed inset-0 flex items-center justify-center p-4 sm:p-6">
        <TransitionChild
          as="div"
          enter="ease-out duration-300"
          enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          enter-to="opacity-100 translate-y-0 sm:scale-100"
          leave="ease-in duration-200"
          leave-from="opacity-100 translate-y-0 sm:scale-100"
          leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          class="flex items-center justify-center w-full"
        >
          <DialogPanel
            class="relative transform rounded-lg bg-white px-4 pb-6 pt-5 text-left shadow-xl transition-all w-full max-w-3xl max-h-[90vh] overflow-y-auto"
          >

            <!-- Close button (top right) -->
            <button
              type="button"
              class="absolute right-4 top-4 text-gray-400 hover:text-gray-600"
              @click="$emit('close')"
            >
              <span class="sr-only">{{ $gettext("Fermer") }}</span>
              <!-- Heroicon X -->
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                class="h-6 w-6"
              >
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <div class="flex items-center mb-4 space-x-2">
              <button
                type="button"
                class="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none"
                @click="printModal"
              >
                {{ $gettext("Imprimer") }}
              </button>

              <button
                type="button"
                class="inline-flex items-center rounded-md bg-green-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-green-700 focus:outline-none"
                @click="downloadPDF"
              >
                {{ $gettext("Télécharger PDF") }}
              </button>
            </div>
            <!-- Printable content -->
            <div ref="printSection">
              <slot />
            </div>
          </DialogPanel>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup>
import { Dialog, DialogPanel, TransitionChild, TransitionRoot } from "@headlessui/vue";
import { ref } from "vue";
import html2pdf from "html2pdf.js";

const props = defineProps({
  open: { type: Boolean, required: true },
  reportType: { type: String, default: "Company" },
  fileName: { type: String, default: null }
});

defineEmits(["close"]);

const printSection = ref(null);

const printModal = () => {
  if (!printSection.value) return;

  const printContents = printSection.value.innerHTML;
  const styles = Array.from(document.querySelectorAll("style, link[rel=stylesheet]"))
    .map(node => node.outerHTML)
    .join("\n");

  const iframe = document.createElement("iframe");
  iframe.style.position = "absolute";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  document.body.appendChild(iframe);

  const doc = iframe.contentDocument || iframe.contentWindow.document;
  doc.open();
  doc.write(`
    <html>
      <head>
        <title>${props.reportType} Report</title>
        ${styles}
        <style>
          @page { size: A4; margin: 20mm; }
          body { font-family: sans-serif; color: #111; }
        </style>
      </head>
      <body>
        ${printContents}
      </body>
    </html>
  `);
  doc.close();

  iframe.onload = () => {
    iframe.contentWindow.focus();
    iframe.contentWindow.print();
    setTimeout(() => document.body.removeChild(iframe), 1000);
  };
};

const downloadPDF = () => {
  if (!printSection.value) return;

  const element = printSection.value;
  const filename = props.fileName || `${props.reportType}-Report.pdf`;
  html2pdf()
    .from(element)
    .set({
      margin: 10,
      filename: filename,
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
    })
    .save();
};
</script>

<style>
.nobreak {
  page-break-inside: avoid;
  break-inside: avoid;
  -webkit-column-break-inside: avoid;
  -moz-column-break-inside: avoid;
}
</style>
